import { TaskListItem, ScheduleEntry } from '@myapp/contract'
import { Fragment, useRef, useMemo } from 'react'
import { detectTransfers } from '../../../shared/lib/schedule'
import {
  CAL_HOURS,
  CAL_TOTAL_HEIGHT,
  CAL_HEADER_HEIGHT,
  HOUR_HEIGHT,
  CAL_START_HOUR,
  CAL_END_HOUR,
  getHourFromIso,
  spanTopPx,
} from '../model/calendarConstants'
import { buildDayBlocks } from '../model/buildDayBlocks'
import { resolveBlockView } from '../model/blockView'
import { resolveCalendarPolicy } from '../model/calendarPolicy'
import { CurrentTimeLine } from './CurrentTimeLine'
import { WorkerScheduleBlock } from './WorkerScheduleBlock'
import type { DragState } from '../model/useCalendarBlockDrag'
import type { TechnicianScheduleView } from '../../../entities/schedule'

/** Overlapping blocks cascade to the right and stack, Notion-style, rather than splitting width. */
const INDENT_PX = 16
const laneLeft = (lane: number) => (lane === 0 ? '2px' : `${2 + lane * INDENT_PX}px`)

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
const MONTH_FORMATTER = new Intl.DateTimeFormat('de-DE', { month: 'short' })

interface Props {
  dayIso: string
  isToday: boolean
  technicianData: TechnicianScheduleView | null
  currentTimePx: number | null
  /** From `useClockMinute` — drives the live edge of a running block. */
  nowMs?: number
  currentUserId: number
  onAppointmentClick: (id: number) => void
  /**
   * Takes the **absence** id, not `entry.id`. See `resolveBlockTarget` on the board: an absence
   * over three technicians is three entries behind one parent row, and the Zeitmanagement edit
   * route loads its param with `useAbsenceDetail`.
   */
  onAbsenceClick: (absenceId: number) => void
  scheduledTasks?: TaskListItem[]
  onTaskClick?: (taskId: number) => void
  drag?: DragState | null
  onBreakPointerDown?: (dayIso: string, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => void
  onTMPointerDown?: (entry: ScheduleEntry, e: React.PointerEvent<HTMLDivElement>, gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => void
}

export const DayColumn = ({
  dayIso,
  isToday,
  technicianData,
  currentTimePx,
  nowMs,
  currentUserId,
  onAppointmentClick,
  onAbsenceClick,
  scheduledTasks = [],
  onTaskClick,
  drag = null,
  onBreakPointerDown,
  onTMPointerDown,
}: Props) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const date = new Date(dayIso + 'T12:00:00')
  const dow = date.getDay()
  const dayName = DAY_NAMES[dow]
  const dayNum = date.getDate()
  const monthName = MONTH_FORMATTER.format(date)

  /*
   * One pass over the day, shared with the widget's drop handler and the conflict check. Day
   * filtering, shift clamping, break length and lane assignment all happen in there — this
   * component now only decides how to paint what comes back.
   */
  const day = useMemo(
    () => buildDayBlocks({ technicianData, scheduledTasks, dayIso, nowMs }),
    [technicianData, scheduledTasks, dayIso, nowMs],
  )
  const { working, lanes } = day

  const transfer = useMemo(
    () => ({
      transfers: detectTransfers(
        day.blocks.map((block) => ({
          key: block.key,
          isJob: block.kind === 'APPOINTMENT' || block.kind === 'TASK',
          startMin: block.span.startMin,
          travelMinutes: block.travelMinutes,
          departureMinutes: block.departureMinutes,
        })),
      ),
      nowMs,
    }),
    [day.blocks, nowMs],
  )

  const shiftStartHour = technicianData?.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
  const shiftEndHour = technicianData?.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null

  const preShiftHeight =
    shiftStartHour != null && shiftStartHour > CAL_START_HOUR
      ? (shiftStartHour - CAL_START_HOUR) * HOUR_HEIGHT
      : 0

  const postShiftTop =
    shiftEndHour != null && shiftEndHour < CAL_END_HOUR
      ? (shiftEndHour - CAL_START_HOUR) * HOUR_HEIGHT
      : CAL_TOTAL_HEIGHT

  return (
    <div className="flex flex-col flex-1 min-w-0 border-l border-gray-200">
      {/* Sticky day header */}
      <div
        className={`sticky top-0 z-50 flex flex-col items-center py-2 border-b border-gray-200 ${
          isToday ? 'bg-blue-50' : 'bg-white'
        }`}
        style={{ height: CAL_HEADER_HEIGHT }}
      >
        <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
          {dayName}
        </span>
        <span
          className={`text-2xl font-bold leading-tight ${
            isToday ? 'text-blue-700' : 'text-gray-800'
          }`}
        >
          {dayNum}
        </span>
        <span className="text-xs text-gray-400">{monthName}</span>
      </div>

      {/* Time track — carries data-planning-grid for cross-day drag detection */}
      <div
        ref={gridRef}
        className="relative"
        style={{ height: CAL_TOTAL_HEIGHT }}
        data-planning-grid="true"
        data-day-iso={dayIso}
      >
        {/*
          Hour rules, and a lighter one on the half hour. The half hour is affordable only now that
          an hour is 96px tall, and it is what lets a block's start be read off the grid — most jobs
          here start at :30, and tracing one back to the axis three columns away is the thing this
          calendar was worst at.
        */}
        {CAL_HOURS.map((h, i) => (
          <Fragment key={h}>
            <div
              className="absolute left-0 right-0 border-t border-gray-200"
              style={{ top: i * HOUR_HEIGHT }}
            />
            {i < CAL_HOURS.length - 1 && (
              <div
                className="absolute left-0 right-0 border-t border-gray-100"
                style={{ top: i * HOUR_HEIGHT + HOUR_HEIGHT / 2 }}
              />
            )}
          </Fragment>
        ))}

        {!working ? (
          <div className="absolute inset-0 bg-gray-300/50 z-[1]" />
        ) : (
          <>
            {preShiftHeight > 0 && (
              <div
                className="absolute left-0 right-0 top-0 bg-gray-300/50 z-[1]"
                style={{ height: preShiftHeight }}
              />
            )}
            {postShiftTop < CAL_TOTAL_HEIGHT && (
              <div
                className="absolute left-0 right-0 bottom-0 bg-gray-300/50 z-[1]"
                style={{ top: postShiftTop }}
              />
            )}
          </>
        )}

        {/*
          One pass over every kind, in the order `buildDayBlocks` produced them, through one block
          component. What each kind *says* is `resolveBlockView`'s; what it *does* — open, drag, or
          nothing — is decided here, because the policy needs the current user and the drag state.
        */}
        {day.blocks.map((block) => {
          const view = resolveBlockView(block, transfer)
          if (!view) return null

          const lane = lanes.get(block.key) ?? 0
          const left = laneLeft(lane)
          const zIndex = 10 + lane

          const startDrag = (
            e: React.PointerEvent<HTMLDivElement>,
            begin: (gridEl: HTMLElement, blockTopPx: number, anchorRect: DOMRect) => void,
          ) => {
            const gridEl = gridRef.current
            if (!gridEl) return
            begin(gridEl, spanTopPx(block.span), e.currentTarget.getBoundingClientRect())
          }

          if (block.kind === 'APPOINTMENT' && block.appointment) {
            const id = block.appointment.id
            return (
              <WorkerScheduleBlock
                key={block.key}
                view={view}
                span={block.span}
                left={left}
                zIndex={zIndex}
                onActivate={() => onAppointmentClick(id)}
              />
            )
          }

          if (block.kind === 'TASK' && block.task) {
            const id = block.task.id
            return (
              <WorkerScheduleBlock
                key={block.key}
                view={view}
                span={block.span}
                left={left}
                zIndex={zIndex}
                onActivate={() => onTaskClick?.(id)}
              />
            )
          }

          if (block.kind === 'ABSENCE' && block.entry) {
            const entry = block.entry
            const { canDrag } = resolveCalendarPolicy(block, { currentUserId })
            const isAssigned = entry.technicianId === currentUserId
            const isDragging = drag?.type === 'tm' && drag.entryId === entry.id && drag.dayIso === dayIso
            // Hidden only in the column the drag started from, not every column it is not over.
            const isDraggedAway =
              drag?.type === 'tm' &&
              drag.entryId === entry.id &&
              drag.originDayIso === dayIso &&
              drag.dayIso !== dayIso
            if (isDraggedAway) return null

            return (
              <WorkerScheduleBlock
                key={block.key}
                view={view}
                span={block.span}
                left={left}
                zIndex={zIndex}
                onActivate={
                  !canDrag && isAssigned && entry.absenceId != null
                    ? () => onAbsenceClick(entry.absenceId!)
                    : undefined
                }
                isDragging={isDragging}
                overrideTopPx={isDragging ? drag.topPx : undefined}
                onPointerDown={
                  canDrag
                    ? (e) =>
                        startDrag(e, (gridEl, topPx, rect) =>
                          onTMPointerDown?.(entry, e, gridEl, topPx, rect),
                        )
                    : undefined
                }
              />
            )
          }

          if (block.kind === 'BREAK') {
            const isDragging = drag?.type === 'break' && drag.dayIso === dayIso
            const isDraggedAway =
              drag?.type === 'break' && drag.originDayIso === dayIso && drag.dayIso !== dayIso
            if (isDraggedAway) return null

            return (
              <WorkerScheduleBlock
                key={block.key}
                view={view}
                span={block.span}
                left={left}
                zIndex={zIndex}
                isDragging={isDragging}
                overrideTopPx={isDragging ? drag.topPx : undefined}
                onPointerDown={(e) =>
                  startDrag(e, (gridEl, topPx, rect) =>
                    onBreakPointerDown?.(dayIso, e, gridEl, topPx, rect),
                  )
                }
              />
            )
          }

          return null
        })}

        {isToday && currentTimePx !== null && (
          <CurrentTimeLine topPx={currentTimePx} />
        )}
      </div>
    </div>
  )
}
